import React, { RefObject } from 'react'
import { StickyTable } from 'react-sticky-table'
import { styled } from '../styled-components'
import { CellHeight, TableStickyProps } from './types'
import { TableContextProvider } from './TableContext'

export interface TableProps {
  border?: 'full' | 'header'
  cellHeight?: CellHeight
  children: React.ReactNode
  'data-test-id'?: string
  headCellHeight?: CellHeight
  isNoCellRightBorders?: boolean
  isTableLayoutFixed?: boolean
  sticky?: TableStickyProps
  wrapperRef?: RefObject<HTMLDivElement>
  zIndex?: number
}

const defaultStickyProp = {
  leftStickyColumnCount: 0,
  borderWidth: '1px',
}

const StickyTableStyled = styled(StickyTable)`
  width: 100%;
  max-height: 100%;
  height: auto !important; /* sticky table sets 100% which makes it impossible to get a container with height of the table */
  position: relative; /* required for pseudo elements */
  z-index: ${props => props.zIndex ?? 'auto'};
  ${props =>
    props.border &&
    `
  border: 1px solid ${props.theme.colors['neutral-light-40']};
  border-radius: ${props.theme.radii[8]};
  `}

  ${props =>
    props.border === 'header' &&
    `
  border-top: none;
  border-top-left-radius: 0;
  border-top-right-radius: 0;
  `}

  .sticky-table-table {
    ${props => (props.isTableLayoutFixed ? 'table-layout: fixed;' : '')}
    width: 100%;
    position: relative; /* required for pseudo elements */
    z-index: 2;
  }
`

export function Table({
  children,
  sticky,
  wrapperRef,
  border,
  zIndex,
  cellHeight = 'regular',
  headCellHeight = 'condensed',
  isNoCellRightBorders = false,
  isTableLayoutFixed = false,
  'data-test-id': testId,
}: TableProps) {
  return (
    <TableContextProvider
      value={{ cellHeight, headCellHeight, isTableLayoutFixed, isNoCellRightBorders, sticky }}
    >
      <StickyTableStyled
        isTableLayoutFixed={isTableLayoutFixed}
        {...{ ...defaultStickyProp, ...sticky }}
        wrapperRef={wrapperRef}
        border={border}
        zIndex={zIndex}
        role='table'
        data-test-id={testId}
      >
        {children}
      </StickyTableStyled>
    </TableContextProvider>
  )
}
